// Client for release-service /mobile/* (Mobile Apps page). Same host + auth as
// releaseApi.ts (shared `platform` cluster, base path /release, bare token).
import type {
  MobileAppId, MobilePlatform, ReleaseEnv, MobileUpdateRules, MobileWebDeploy, MobileAppMenu,
  MobileAppMenuEntry, StartMobileDeployRequest,
} from '@platform/shared-types';
import { releaseHost, releaseAuthHeader } from '../../api/releaseApi';

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${releaseHost()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...releaseAuthHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    let detail = '';
    try { const b = await res.json(); detail = b?.message || b?.error || ''; } catch { /* not JSON */ }
    throw new Error(detail || `release ${res.status}`);
  }
  return res.json();
}
const q = (o: Record<string, string>) => new URLSearchParams(o).toString();

export interface MobileBuild {
  commit: string; commitMessage?: string; nativeFingerprint: string; builtAt: number; registeredAt?: number;
  kind: Record<MobilePlatform, 'web' | 'native'>;
}
export interface NativeInfo { fingerprint: string; nativeBuild: number; nativeVersion: string }
export interface MobileStats {
  phones: number;
  byBundle: { bundleId: string; phones: number }[];
  byNativeBuild: { nativeBuild: number; phones: number }[];
  permissions: { notifications: number; camera: number; biometrics: number };
  truncated?: boolean;
}
export interface MobileRun {
  id: number; status: string; conclusion: string | null; createdAt: string; url: string; title: string; commit: string;
  steps: { name: string; status: string; conclusion: string | null }[];
}

export const mobileApi = {
  rules: (app: MobileAppId, env: ReleaseEnv, platform: MobilePlatform) =>
    call<{ rules: MobileUpdateRules | null }>(`/mobile/rules?${q({ app, env, platform })}`),
  saveRules: (app: MobileAppId, env: ReleaseEnv, platform: MobilePlatform, rules: Omit<MobileUpdateRules, 'updatedAt' | 'updatedBy'>) =>
    call<{ rules: MobileUpdateRules }>('/mobile/rules', { method: 'PUT', body: JSON.stringify({ app, env, platform, rules }) }),
  deploy: (app: MobileAppId, env: ReleaseEnv, platform: MobilePlatform) =>
    call<{ deploy: MobileWebDeploy | null }>(`/mobile/deploys?${q({ app, env, platform })}`),
  patchDeploy: (b: { app: MobileAppId; env: ReleaseEnv; platform: MobilePlatform; deployId: string; percent?: number; status?: MobileWebDeploy['status'] }) =>
    call<{ deploy: MobileWebDeploy }>('/mobile/deploys', { method: 'PATCH', body: JSON.stringify(b) }),
  appMenu: (app: MobileAppId, env: ReleaseEnv) => call<{ menu: MobileAppMenu | null }>(`/mobile/app-menu?${q({ app, env })}`),
  saveAppMenu: (app: MobileAppId, env: ReleaseEnv, entries: MobileAppMenuEntry[]) =>
    call<{ menu: MobileAppMenu }>('/mobile/app-menu', { method: 'PUT', body: JSON.stringify({ app, env, entries }) }),
  builds: (app: MobileAppId, env: ReleaseEnv) =>
    call<{ builds: MobileBuild[]; native: Record<MobilePlatform, NativeInfo | null> }>(`/mobile/builds?${q({ app, env })}`),
  setNative: (b: { app: MobileAppId; env: ReleaseEnv; platform: MobilePlatform; fingerprint: string; nativeBuild: number; nativeVersion: string }) =>
    call<{ native: NativeInfo }>('/mobile/native', { method: 'PUT', body: JSON.stringify(b) }),
  stats: (app: MobileAppId, env: ReleaseEnv, platform: MobilePlatform) => call<MobileStats>(`/mobile/stats?${q({ app, env, platform })}`),
  startDeploy: (b: StartMobileDeployRequest) => call<{ dispatched: true }>('/mobile/deploys', { method: 'POST', body: JSON.stringify(b) }),
  runs: (app: MobileAppId) => call<{ runs: MobileRun[] }>(`/mobile/runs?${q({ app })}`),
};
